import useSWR from 'swr';
import { Users, EuroIcon, UserCheck, TrendingUp, AlertTriangle, Bell } from 'lucide-react';
import { StatCard } from '@/components/StatCard';
import apiClient from '@/lib/api';

// ─── Tipos de notificaciones ──────────────────────────────────────────────────

interface Notification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

// ─── Widget: Próximos vencimientos de personal ────────────────────────────────

async function notifFetcher(url: string): Promise<Notification[]> {
  const res = await apiClient.get<{ data: Notification[] }>(url);
  return res.data.data;
}

function ExpiringContractsWidget() {
  const { data, isLoading } = useSWR<Notification[]>(
    '/notifications',
    notifFetcher,
    { refreshInterval: 60_000 },
  );

  const expiries = (data ?? []).filter(n => n.type === 'contract_expiry' && !n.read_at);

  // Renderizar siempre un contenedor estable — evita el removeChild de React
  // cuando el componente transiciona de "cargando" a "sin datos" (null vs nodo).
  const visible = isLoading || expiries.length > 0;

  return (
    <section style={{ display: visible ? 'block' : 'none' }}>
      <h2 style={{
        fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)',
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.75rem',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Bell size={12} />
        Proximos vencimientos de personal
      </h2>
      <div style={{
        background: 'var(--color-surface)',
        border: '1px solid color-mix(in srgb, var(--color-warning) 35%, transparent)',
        borderLeft: '3px solid var(--color-warning)',
        borderRadius: 10, overflow: 'hidden',
      }}>
        {isLoading ? (
          <div style={{ padding: '1rem', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
            Cargando...
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-table-header)' }}>
                {['Empleado', 'Detalle', 'Dias restantes'].map(h => (
                  <th key={h} style={{ padding: '0.5rem 1rem', textAlign: 'left', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expiries.map((n, i) => {
                const daysMatch = n.body?.match(/en (\d+) d[íi]as/);
                const days = daysMatch ? Number(daysMatch[1]) : null;
                const urgent = days !== null && days <= 7;
                return (
                  <tr key={n.id} style={{ borderBottom: i < expiries.length - 1 ? '1px solid var(--color-border)' : 'none' }}>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', color: 'var(--color-text)', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {urgent
                          ? <AlertTriangle size={13} style={{ color: 'var(--color-danger)', flexShrink: 0 }} />
                          : null
                        }
                        <span>{n.title.replace('Contrato proximo a vencer: ', '')}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: 'var(--color-muted)' }}>
                      <span>{n.body?.replace(/^\[[\w-]+\]\s*/, '') ?? '—'}</span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600,
                        background: urgent
                          ? 'color-mix(in srgb, var(--color-danger) 12%, transparent)'
                          : 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
                        color: urgent ? 'var(--color-danger)' : 'var(--color-warning)',
                      }}>
                        {days !== null ? `${days} dias` : '< 30 dias'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// ─── Tipos que espera el endpoint /api/v1/rrhh/employees/stats ────────────────
interface EmployeeByState {
  estado: string;
  count: number;
  salario_total: number;
}

interface EmployeeStats {
  total: number;
  porEstado: EmployeeByState[];
  totalSalarioBase: number;
  costeTotalEstimado: number;   // +30% SS
  currency: string;
}

// ─── Fetcher para SWR ─────────────────────────────────────────────────────────
async function fetcher(url: string): Promise<EmployeeStats> {
  const res = await apiClient.get<{ data: EmployeeStats }>(url);
  return res.data.data;
}

// ─── Helpers de formato ───────────────────────────────────────────────────────
function formatCurrency(value: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

// ─── Componente ───────────────────────────────────────────────────────────────
export function DashboardHome() {
  const { data, error, isLoading } = useSWR<EmployeeStats>(
    '/rrhh/employees/stats',
    fetcher,
    { refreshInterval: 60_000 },
  );

  const activeCount =
    data?.porEstado?.find((e) => e.estado === 'activo')?.count ?? 0;

  const hasError = !!error;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {/* Encabezado de página */}
      <div>
        <h1
          style={{
            fontSize: '1.25rem',
            fontWeight: 700,
            color: 'var(--color-text)',
            margin: 0,
          }}
        >
          Resumen general
        </h1>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-muted)',
            marginTop: '0.25rem',
          }}
        >
          Vista consolidada de los indicadores clave del negocio.
        </p>
      </div>

      {/* Error de carga */}
      {hasError && (
        <div
          role="alert"
          style={{
            background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)',
            borderRadius: 8,
            padding: '0.875rem 1rem',
            fontSize: '0.8125rem',
            color: 'var(--color-danger)',
          }}
        >
          No se han podido cargar las estadisticas de RRHH. Comprueba la
          conexion con el servidor.
        </div>
      )}

      {/* Seccion RRHH */}
      <section>
        <h2
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.75rem',
          }}
        >
          Recursos Humanos
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '1rem',
          }}
        >
          <StatCard
            title="Total Empleados"
            value={data?.total ?? 0}
            subtitle="Plantilla registrada"
            icon={<Users size={16} />}
            loading={isLoading}
          />
          <StatCard
            title="Empleados Activos"
            value={activeCount}
            subtitle={
              data
                ? `${Math.round((activeCount / data.total) * 100)}% de la plantilla`
                : undefined
            }
            icon={<UserCheck size={16} />}
            accentColor="var(--color-success)"
            loading={isLoading}
          />
          <StatCard
            title="Masa Salarial"
            value={data ? formatCurrency(data.totalSalarioBase, data.currency) : '—'}
            subtitle="Salario bruto mensual"
            icon={<EuroIcon size={16} />}
            accentColor="var(--color-warning)"
            loading={isLoading}
          />
          <StatCard
            title="Coste Empresa"
            value={data ? formatCurrency(data.costeTotalEstimado, data.currency) : '—'}
            subtitle="Incluye SS (+30% estimado)"
            icon={<TrendingUp size={16} />}
            accentColor="var(--color-danger)"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Widget vencimientos de personal */}
      <ExpiringContractsWidget />

      {/* Desglose por estado — contenedor siempre presente para evitar removeChild */}
      <section style={{ display: !isLoading && (data?.porEstado?.length ?? 0) > 0 ? 'block' : 'none' }}>
        <h2
          style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--color-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: '0.75rem',
          }}
        >
          Desglose por estado
        </h2>
        <div
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid var(--color-border)',
                  background: 'var(--color-table-header)',
                }}
              >
                {['Estado', 'Empleados', 'Masa salarial'].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '0.625rem 1rem',
                      textAlign: 'left',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: 'var(--color-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.porEstado ?? []).map((row, i) => (
                <tr
                  key={row.estado}
                  style={{
                    borderBottom:
                      i < (data?.porEstado.length ?? 0) - 1
                        ? '1px solid var(--color-border)'
                        : 'none',
                  }}
                >
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <EstadoBadge estado={row.estado} />
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-text)',
                      fontWeight: 600,
                    }}
                  >
                    {row.count}
                  </td>
                  <td
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.875rem',
                      color: 'var(--color-muted)',
                    }}
                  >
                    {data ? formatCurrency(row.salario_total, data.currency) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

// ─── Badge de estado ──────────────────────────────────────────────────────────
function EstadoBadge({ estado }: { estado: string }) {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    activo:     { label: 'Activo',     color: 'var(--color-success)', bg: 'color-mix(in srgb, var(--color-success) 12%, transparent)' },
    baja:       { label: 'Baja',       color: 'var(--color-danger)',  bg: 'color-mix(in srgb, var(--color-danger) 12%, transparent)' },
    excedencia: { label: 'Excedencia', color: 'var(--color-warning)', bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)' },
  };
  const cfg = map[estado] ?? { label: estado, color: 'var(--color-muted)', bg: 'transparent' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: '0.75rem',
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        borderRadius: 4,
        padding: '2px 8px',
        textTransform: 'capitalize',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: cfg.color,
          flexShrink: 0,
        }}
      />
      {cfg.label}
    </span>
  );
}
